import http from "services/http";

export interface HotelTransaction {
  _id: string;
  paymentReference: string;
  transactionReference?: string;
  type: string;
  amount: number;
  charges: number;
  active_bank: string;
  status: string;
  discount: boolean;
  accountNumber?: string;
  accountName?: string;
  bankName?: string;
  expiresOn?: string;
  transactionDate: string;
  createdAt: string;
}

export interface HotelBookingGuestGroup {
  adults: number;
  children: unknown[];
}

export interface HotelContactInfo {
  email: string;
  phone: string;
  name: string;
}

export interface HotelGuestName {
  first_name: string;
  last_name: string;
  is_child?: boolean;
}

export interface HotelEtgResponse {
  status?: string;
  error?: string | null;
  data?: Record<string, any>;
  debug?: unknown;
}

export interface HotelBooking {
  _id: string;
  partner_order_id: string;
  // Populated in the admin list; plain ids on the public booking lookup.
  transaction_id: (HotelTransaction | string)[];
  status: string;
  hotel_id: number;
  hotel_name: string;
  checkin: string;
  checkout: string;
  rooms: number;
  guests: HotelBookingGuestGroup[];
  total_price: number;
  currency: string;
  total_price_usd: number;
  usd_ngn_rate: number;
  contact_info: HotelContactInfo;
  guest_details?: { rooms?: { guests?: HotelGuestName[] }[] };
  etg_response?: HotelEtgResponse | null;
  payment_type?: string;
  prebook_hash?: string;
  prebook_expires_at?: string;
  createdAt: string;
}

export interface GetHotelBookingsResponse {
  success: boolean;
  message: string;
  data: {
    bookings: HotelBooking[];
    total: number;
    page: number;
    limit: number;
  };
}

export const HOTEL_STATUS_TONES = {
  pending_payment: "pending",
  processing: "info",
  confirmed: "success",
  cancelled: "muted",
  failed: "error",
} as const;

export const HOTEL_STATUS_LABELS: Record<string, string> = {
  pending_payment: "Awaiting payment",
  processing: "Finalizing",
};

export const HOTEL_STATUSES = Object.keys(HOTEL_STATUS_TONES);

export const getHotelBookings = async (
  page = 1,
  limit = 20,
  status?: string,
): Promise<GetHotelBookingsResponse> => {
  const response = await http.get("/hotel/admin/bookings", {
    params: { page, limit, ...(status ? { status } : {}) },
  });
  return response.data;
};

// There is no admin detail endpoint; this is the public booking lookup.
export const getHotelBooking = async (partnerOrderId: string): Promise<HotelBooking> => {
  const response = await http.get(`/hotel/booking/${encodeURIComponent(partnerOrderId)}`);
  return response.data?.data;
};

export const cancelHotelBooking = async (partnerOrderId: string) => {
  const response = await http.post(
    `/hotel/booking/${encodeURIComponent(partnerOrderId)}/cancel`,
  );
  return response.data;
};

export const syncHotels = async (full = false) => {
  const response = await http.post("/hotel/admin/sync-hotels", undefined, {
    params: { full },
  });
  return response.data;
};

export const syncRegions = async () => {
  const response = await http.post("/hotel/admin/sync-regions");
  return response.data;
};

export const checkBookingStatus = async (partnerOrderId: string) => {
  const response = await http.post("/hotel/admin/check-booking-status", {
    partner_order_id: partnerOrderId,
  });
  return response.data;
};

export const nightsBetween = (checkin: string, checkout: string) => {
  const ms = new Date(checkout).getTime() - new Date(checkin).getTime();
  return Number.isFinite(ms) ? Math.max(Math.round(ms / 86400000), 0) : 0;
};

export const totalGuests = (guests?: HotelBookingGuestGroup[]) =>
  (guests || []).reduce((sum, g) => sum + (g.adults || 0) + (g.children?.length || 0), 0);
